import { observable, action, computed } from 'mobx'
import {
  CreateOrEditDiscount,
  DiscountCategory,
  DiscountOption,
  DiscountProduct,
  IBaseCategory,
  IBaseProduct,
} from '@getgreenline/homi-shared'
import { ProductStore } from '../../../../../stores/CurrentCompanyStore/ProductStore'

export type TDiscountSearchType = 'product' | 'category'

type TDiscountCategoryWithIndex = DiscountCategory & { index: number }

export class DiscountStore {
  @observable
  mappedFlattenedCategories: Map<string, IBaseCategory> = new Map()

  @observable
  mappedSelectedCategories: Map<string, TDiscountCategoryWithIndex> = new Map()

  @observable
  mappedParentProductName = observable.map<string, string | null>()

  constructor(private discount: CreateOrEditDiscount, private productStore: ProductStore) {}

  // Should be called after productStore.getCategories()
  @action
  setFlattenedMappedCategory = () => {
    const flattenedCategories = this.productStore.flattenedCategories

    flattenedCategories.forEach((category) => {
      this.mappedFlattenedCategories.set(category.id, category)
    })
  }

  @action
  setMappedSelectedCategory = () => {
    this.discount.categories.forEach((category, index) => {
      const selectedCategoryWithIndex = { ...category, index }
      this.mappedSelectedCategories.set(category.id, selectedCategoryWithIndex)
    })
  }

  @action
  addNewDiscountByProduct = (discountOption: DiscountOption, product: IBaseProduct) => {
    const newDiscountProduct: DiscountProduct = {
      type: discountOption,
      id: product.id,
      name: product.name,
      parentProductId: product.parentProductId,
    }

    this.discount.addProductWithData(newDiscountProduct)

    if (product.parentProductId) {
      this.mappedParentProductName.set(product.parentProductId, product.parentProductName)
    }
  }

  @action
  addNewDiscountByCategory = (discountOption: DiscountOption, categoryId: string) => {
    const category = this.mappedFlattenedCategories.get(categoryId)

    if (category) {
      const newDiscountCategory = {
        type: discountOption,
        id: category.id,
        name: category.name,
        parentCategoryId: category.parentCategoryId || null,
        colorHex: category.colorHex || null,
      }

      this.discount.addCategoryWithData(newDiscountCategory)
      this.setMappedSelectedCategory()
    }
  }

  @action
  removeDiscountCategory = (category: DiscountCategory) => {
    const matchingCategory = this.mappedSelectedCategories.get(category.id)

    if (matchingCategory) {
      this.discount.removeCategory(matchingCategory.index)
      this.mappedSelectedCategories.delete(matchingCategory.id)
      this.setMappedSelectedCategory()
    }
  }

  getSelectedProductIds = (discountOption: DiscountOption) => {
    const data = discountOption === 'include' ? this.includedProducts : this.excludedProducts
    const productIds: string[] = []

    data.forEach((p) => {
      p.id && productIds.push(p.id)
    })

    return productIds
  }

  getExcludedChildCategories = (selectedCategory: DiscountCategory) =>
    this.excludedCategories.filter((category) => category.parentCategoryId === selectedCategory.id)

  @computed
  get includedProducts(): DiscountProduct[] {
    return this.filterByDiscountOption(this.discount.products, 'include')
  }

  @computed
  get excludedProducts(): DiscountProduct[] {
    return this.filterByDiscountOption(this.discount.products, 'exclude')
  }

  @computed
  get includedCategories(): DiscountCategory[] {
    return this.filterByDiscountOption(this.discount.categories, 'include')
  }

  @computed
  get excludedCategories(): DiscountCategory[] {
    return this.filterByDiscountOption(this.discount.categories, 'exclude')
  }

  @computed
  get isParentCategoryIncluded() {
    return this.includedCategories.some((category) => !category.parentCategoryId)
  }

  private filterByDiscountOption = <T extends DiscountProduct | DiscountCategory>(
    data: T[],
    option: DiscountOption,
  ) => {
    const filtered = data.filter((d) => d.id && d.type === option)

    return filtered
  }
}
